import { useEffect, type RefObject } from 'react';
import { StyleSheet } from 'react-native';

/**
 * CORE-1724 — the time wheel on a touch-screen web viewport (iOS Safari first).
 *
 * The native wheel leans on `snapToInterval` and `onMomentumScrollEnd`; neither
 * exists in react-native-web, so on the web a swipe moved the column freely,
 * stopped between rows and never changed the chosen time — only a tap did. The
 * browser's own scroll snapping does the snapping here, and the column picks
 * whatever row it settles on once the scroll goes quiet.
 *
 * The page behind the sheet must stay put. QA 21.10 (iPhone and iPad, Safari
 * and Chrome): with `touch-action: none` on the sheet the page still moved and
 * the columns would not swipe — WebKit's handling of `touch-action` on the
 * ancestors of a scroll container is not something to lean on. So the backdrop
 * holds the page with a JS touch lock instead: a non-passive `touchmove` on the
 * backdrop. QA 22.10 (same devices): the columns still would not swipe on
 * native scroll, so they are driven by our own code now (see touchWheel.ts) and
 * nothing in the sheet needs the browser's scroll — every move is cancelled.
 */
export const webWheelStyles = StyleSheet.create({
  col: { scrollSnapType: 'y mandatory', overscrollBehavior: 'contain' } as any,
  row: { scrollSnapAlign: 'center' } as any,
});

/** Quiet time after the last scroll event that counts as «the wheel stopped». */
export const WHEEL_SETTLE_MS = 120;

/**
 * A finger screen (phone or tablet): the wheel and the lock are needed on an
 * iPad in landscape too, which is wider than the phone breakpoint.
 */
export function isCoarsePointerWeb(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/** The touch lock on the sheet's backdrop; returns the detach function. */
export function attachWheelTouchLock(root: HTMLElement): () => void {
  const onMove = (e: TouchEvent) => {
    if (e.cancelable) e.preventDefault();
  };
  root.addEventListener('touchmove', onMove, { passive: false });
  return () => root.removeEventListener('touchmove', onMove);
}

/** Lock while the sheet is open; on react-native-web a View ref IS the DOM node. */
export function useWheelTouchLock(ref: RefObject<unknown>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const node = ref.current as HTMLElement | null;
    if (!node || typeof node.addEventListener !== 'function') return;
    return attachWheelTouchLock(node);
  }, [ref, enabled]);
}
